import { useRef, useState } from 'react';
import type { ActiveSession } from '../shared/types';
import { orderedExercises } from '../shared/engine';
import { api } from './api';
import { syncBeforeEdit } from './RecordEditor';
import { ErrorNotice, Field, Modal } from './components';

export function WorkoutEditor({ session, profileId, onSaved, onClose, onBusy }: {
  session: ActiveSession; profileId: string; onSaved: (record: ActiveSession) => void;
  onClose: () => void; onBusy: (busy: boolean) => void;
}) {
  const exercises = orderedExercises(session.template);
  const logged = new Set(session.sets.map(set => set.templateExerciseId));
  const [selected, setSelected] = useState<string[]>([]);
  const [tail, setTail] = useState('10');
  const [saveRoutine, setSaveRoutine] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inFlight = useRef(false);
  const attempt = useRef<{ key: string; id: string } | null>(null);
  async function save() {
    if (inFlight.current || !selected.length) return;
    inFlight.current = true; setBusy(true); onBusy(true); setError('');
    const payload = { exerciseIds: selected, expectedSlots: exercises.map(item => item.id), saveRoutine,
      expectedTemplateVersion: session.template.version };
    const key = JSON.stringify(payload);
    if (attempt.current?.key !== key) attempt.current = { key, id: crypto.randomUUID() };
    try {
      await syncBeforeEdit(profileId);
      const record = await api<ActiveSession>(`/records/${session.id}/remove-exercises`, { method: 'POST',
        headers: { 'X-Hardnup-Profile': profileId }, body: JSON.stringify({ ...payload, clientEventId: attempt.current.id }) });
      onSaved(record);
    } catch (error) { setError(error instanceof Error ? error.message : 'Could not update workout.'); }
    finally { inFlight.current = false; setBusy(false); onBusy(false); }
  }
  return <Modal title="Edit workout" onClose={() => { if (!inFlight.current) onClose(); }}>
    <p>Remove exercises you haven’t started. Logged exercises stay saved; use Finish exercise early to stop their remaining sets.</p>
    {error && <ErrorNotice message={error} />}
    <Field label="Number of exercises at the end"><input type="number" inputMode="numeric" min="1" max={exercises.length - 1}
      value={tail} disabled={busy} onChange={event => setTail(event.target.value)} /></Field>
    <button disabled={busy || !Number.isInteger(Number(tail)) || Number(tail) < 1 || Number(tail) >= exercises.length}
      onClick={() => { setSelected(exercises.slice(-Number(tail)).filter(item => !logged.has(item.id)).map(item => item.id)); setError(''); }}>
      Select last {tail || '…'} exercises
    </button>
    <fieldset disabled={busy} className="workout-edit-list"><legend>Preview and choose exercises to remove</legend>
      {exercises.map((item, index) => <label key={item.id}>
        <input type="checkbox" checked={selected.includes(item.id)} disabled={logged.has(item.id)}
          onChange={event => setSelected(old => event.target.checked ? [...old, item.id] : old.filter(id => id !== item.id))} />
        <span>{index + 1}. {item.exercise.name}{logged.has(item.id) ? ' · Logged sets protected' : ''}</span>
      </label>)}
    </fieldset>
    <Field label="Apply changes to"><select value={saveRoutine ? 'routine' : 'today'} disabled={busy} onChange={event => setSaveRoutine(event.target.value === 'routine')}>
      <option value="today">This workout only</option><option value="routine">This workout and saved routine</option>
    </select></Field>
    <p>{selected.length} selected · {exercises.length - selected.length} exercises will remain. {saveRoutine ? 'Future workouts will use the shorter routine.' : 'Your saved routine will stay the same.'}</p>
    <button className="button button--primary button--full" disabled={busy || !selected.length || selected.length >= exercises.length} onClick={() => void save()}>
      {busy ? 'Saving…' : `Remove ${selected.length} exercises`}
    </button>
  </Modal>;
}
